import type { CSSProperties } from 'react';

/**
 * THE money monopoly (docs/DESIGN-SYSTEM.md §12): the only component allowed
 * to render an amount. Formatting — ₹, Indian lakh/crore grouping, compaction
 * with the exact figure preserved, negative glyphing, tabular vs proportional
 * figures, the estimate triple-mark — lives here and nowhere else, so a
 * money-display bug can only ever exist in one file.
 */

export interface MoneyValue {
  amount: number; // integer paise — the API contract guarantees it
  currency: 'INR';
}

export interface ValuationValue {
  money: MoneyValue;
  asOf: string;
  isEstimate: boolean;
  source?: string;
}

/** ₹12,34,567.89 — Indian grouping: last 3 digits, then pairs. */
export function formatExact(paise: number, withDecimals = true): string {
  const negative = paise < 0;
  const abs = Math.abs(paise);
  const rupees = Math.floor(abs / 100);
  const fraction = String(abs % 100).padStart(2, '0');

  const digits = String(rupees);
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3);
  const grouped = rest ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}` : last3;

  return `${negative ? '−' : ''}₹${grouped}${withDecimals ? `.${fraction}` : ''}`;
}

/**
 * Compact form for tight spaces: ₹1.24 Cr / ₹45.2 L / ₹80,500. Compaction
 * starts above ₹1 lakh; the exact figure always rides along as a tooltip.
 */
export function formatCompact(paise: number): string {
  const negative = paise < 0;
  const rupees = Math.abs(paise) / 100;
  const sign = negative ? '−' : '';
  if (rupees >= 1_00_00_000) return `${sign}₹${trim((rupees / 1_00_00_000).toFixed(2))} Cr`;
  if (rupees >= 1_00_000) return `${sign}₹${trim((rupees / 1_00_000).toFixed(2))} L`;
  return formatExact(paise, false);
}

function trim(fixed: string): string {
  return fixed.replace(/\.?0+$/, '');
}

export interface MoneyTextProps {
  value: MoneyValue | ValuationValue;
  /** hero = proportional figures; cell (default) = tabular for column alignment (§3). */
  variant?: 'hero' | 'cell' | 'compact';
  /** Color gains/losses — ALWAYS paired with the sign glyph, never color alone. */
  signed?: boolean;
}

function isValuation(v: MoneyTextProps['value']): v is ValuationValue {
  return 'money' in v;
}

export function MoneyText({ value, variant = 'cell', signed = false }: MoneyTextProps) {
  const money = isValuation(value) ? value.money : value;
  const isEstimate = isValuation(value) && value.isEstimate;

  const text =
    variant === 'compact' ? formatCompact(money.amount) : formatExact(money.amount, variant !== 'hero');
  const exact = formatExact(money.amount);

  const style: CSSProperties = {
    font: variant === 'hero' ? 'var(--type-money-hero)' : 'var(--type-money-cell)',
    // Tabular only where digits must align in columns; hero stays proportional (§3).
    fontVariantNumeric: variant === 'hero' ? 'normal' : 'tabular-nums',
    whiteSpace: 'nowrap',
    ...(signed && money.amount !== 0
      ? { color: money.amount > 0 ? 'var(--color-value-positive)' : 'var(--color-value-negative)' }
      : {}),
    ...(isEstimate
      ? {
          color: 'var(--color-value-estimate)',
          textDecoration: 'underline dashed 1px',
          textUnderlineOffset: '3px',
        }
      : {}),
  };

  // The estimate triple-mark: amber + '~' + dashed underline — all three,
  // always, so meaning never rides on color alone (§2.2).
  const prefix = `${isEstimate ? '~' : ''}${signed && money.amount > 0 ? '+' : ''}`;

  return (
    <span style={style} title={text === exact ? undefined : exact} aria-label={`${prefix}${exact}${isEstimate ? ', estimated' : ''}`}>
      {prefix}
      {text}
    </span>
  );
}
